import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import { extractAISQLAssistSuggestionText } from '../Shared/AISQLAssistUtils';
import { ManagedReportInput } from '../Shared/ManagedReport';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAISingleTurnPromptModelRequest from '../../Interfaces/Requests/AI/GetAISingleTurnPromptModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAISingleTurnPromptModelResponse from '../../Interfaces/Responses/AI/GetAISingleTurnPromptModelResponse';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';

export type ManagedReportInputEditResult = {
    operation: ManagedAdminChildOperation;
    reportInput: ManagedReportInput;
};

type AUDReportInputFormValues = {
    riName: string;
    riTitle: string;
    riSourceQuery: string;
    riSelector: string;
    riRequired: boolean;
    riDataFormat: string;
    riMultiSelect: boolean;
    riSortOrder: number;
    riGlobalDesignator: string;
    riInPageInput: boolean;
    riDefaultValue: string;
};

type AUDReportInputsProps = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    reportInputs: ManagedReportInput[];
    initialSelectedReportInputID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetReportInputs: (
        result: ManagedReportInputEditResult
    ) => void;
    close: () => void;
};

const AUDReportInputs: React.FC<AUDReportInputsProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDReportInput] = Form.useForm();

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);
    const [aiContextAssistContext, setAIContextAssistContext] = useState({
        fieldName: 'riSourceQuery',
        title: 'AI Report Source Query Assist',
        currentSectionTitle: 'Current Source Query',
        requestSectionTitle: 'What do you want this source query to do?',
        resultSectionTitle: 'Suggested Source Query',
        requestPlaceholder: 'Describe the source query you want AI to help build or review.',
        askButtonText: 'Ask AI',
        useButtonText: 'Use Suggestion',
    });

    const reportInputs = props.reportInputs ?? [];

    const getInitialRIMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const formatRIDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const getInitialSelectedReportInput = (): ManagedReportInput | undefined => {
        if (!props.initialSelectedReportInputID || props.initialSelectedReportInputID <= 0) {
            return undefined;
        }

        return reportInputs.find(
            (reportInput) => reportInput.id === props.initialSelectedReportInputID
        );
    };

    const initialSelectedReportInput = getInitialSelectedReportInput();
    const initialRIMode = getInitialRIMode();

    const initialRIFormValues = initialRIMode === 'add'
        ? {
            riTitle: '',
            riGlobalDesignator: '',
            riSelector: 'dropdown',
            riDataFormat: 'string',
            riMultiSelect: false,
            riRequired: false,
            riInPageInput: false,
            riSourceQuery: '',
            riDefaultValue: '',
        }
        : {
            riTitle: initialSelectedReportInput?.title ?? '',
            riGlobalDesignator: initialSelectedReportInput?.globalDesignator ?? '',
            riSelector: initialSelectedReportInput?.selector ?? 'dropdown',
            riDataFormat: initialSelectedReportInput?.dataFormat ?? 'string',
            riMultiSelect: initialSelectedReportInput?.multiSelect ?? false,
            riRequired: initialSelectedReportInput?.required ?? false,
            riInPageInput: initialSelectedReportInput?.inPageInput ?? false,
            riSourceQuery: initialSelectedReportInput?.sourceQuery ?? '',
            riDefaultValue: initialSelectedReportInput?.defaultValue ?? '',
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialRIMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialRIMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialRIMode === 'delete');

    const [riSelectedReportInputID, setRISelectedReportInputID] = useState(initialSelectedReportInput?.id ?? -1);
    const [riSelectedReportInputTitle, setRISelectedReportInputTitle] = useState(initialSelectedReportInput?.title ?? '');

    const [riSelector, setRISelector] = useState<string>(initialSelectedReportInput?.selector ?? 'dropdown');
    const [riDataFormat, setRIDataFormat] = useState<string>(initialSelectedReportInput?.dataFormat ?? 'string');

    const [riSelectedReportInputCreatedBy, setRISelectedReportInputCreatedBy] = useState(initialSelectedReportInput?.createdBy ?? -1);
    const [riSelectedReportInputCreatedTs, setRISelectedReportInputCreatedTs] = useState(initialSelectedReportInput?.createdTs ?? new Date());

    const riPropsReportID = props.reportID > 0 ? props.reportID : 0;
    const riPropsReportName = props.reportName !== '' ? props.reportName : 'New Report';
    const riPropsReportGroupName = props.reportGroupName !== '' ? props.reportGroupName : 'New Report Group';

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-report-input-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_REPORT_INPUT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_REPORT_INPUT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManagedActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for Report Input assistance.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProviderNamePhrase = (): string => {
        return 'SQL Review';
    };

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = getAIContextAssistProviderNamePhrase().toLowerCase();

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase)
        );
    };

    const buildAIContextAssistPrompt = (requestText: string): string => {
        const currentSQLText = formAUDReportInput.getFieldValue(aiContextAssistContext.fieldName) ?? '';

        return [
            'RetainBI SQL assistance request.',
            `Assist area: ${aiContextAssistContext.title}`,
            '',
            'Current SQL/context:',
            currentSQLText,
            '',
            'User request:',
            requestText,
            '',
            'Output instructions:',
            'Return only the clean, drop-in ready Source Query SQL text.',
            'Do not include markdown, explanation, labels, or surrounding commentary in the suggested SQL.',
            'Do not include credentials, connection strings, secrets, PHI, PII, tenant-isolated data, or system security details.',
            'Do not suggest executing SQL. The user must review and apply any suggestion manually.',
        ].join('\n');
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, prompt }: GetAISingleTurnPromptModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AISingleTurnPrompt`;
                const requestData: GetAISingleTurnPromptModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAISingleTurnPromptModelResponse, GetAISingleTurnPromptModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_REPORT_INPUT_AI_CONTEXT_ASSIST: [AI Context Assist] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAISingleTurnPromptModelResponse) => {
                const rawResponseText = response.text ?? '';
                const suggestedSQLText = extractAISQLAssistSuggestionText(rawResponseText);
                const responseWasCleaned = rawResponseText.trim() !== suggestedSQLText.trim();

                setAIContextAssistResultText(suggestedSQLText);
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI suggestion returned successfully.' : 'AI suggestion request did not succeed.',
                    responseWasCleaned ? 'RetainBI removed explanatory text before showing the suggested SQL.' : '',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_REPORT_INPUT_AI_CONTEXT_ASSIST: [AI Context Assist] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'Unable to complete the AI SQL assistance request.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const handleOpenAISourceQueryAssistModal = () => {
        const fieldName = 'riSourceQuery';

        setAIContextAssistContext({
            fieldName: fieldName,
            title: 'AI Report Source Query Assist',
            currentSectionTitle: 'Current Source Query',
            requestSectionTitle: 'What do you want this source query to do?',
            resultSectionTitle: 'Suggested Source Query',
            requestPlaceholder: 'Describe the source query you want AI to help build or review.',
            askButtonText: 'Ask AI',
            useButtonText: 'Use Suggestion',
        });
        setAIContextAssistCurrentText(formAUDReportInput.getFieldValue(fieldName) ?? '');
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                `Unable to find an AI Provider matching ${getAIContextAssistProviderNamePhrase()}.`,
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            prompt: buildAIContextAssistPrompt(requestText),
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const handleUseAIContextAssistSuggestion = (resultText: string) => {
        formAUDReportInput.setFieldValue(aiContextAssistContext.fieldName, resultText);
        setAIContextAssistModalOpen(false);
    };

    const setRIMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getRIOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getRIActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const clearRISelection = () => {
        setRISelectedReportInputID(-1);
        setRISelectedReportInputTitle('');
        setRISelectedReportInputCreatedBy(0);
        setRISelectedReportInputCreatedTs(new Date());

        setRISelector('dropdown');
        setRIDataFormat('string');

        setRIMode('add');
        setRIBlankForm();
    };

    const getSelectedReportInput = (
        selectedReportInputID: number = riSelectedReportInputID
    ): ManagedReportInput | undefined => {
        return reportInputs.find((reportInput) => reportInput.id === selectedReportInputID);
    };

    const setRIBlankForm = () => {
        formAUDReportInput.setFieldValue('riTitle', '');
        formAUDReportInput.setFieldValue('riGlobalDesignator', '');
        formAUDReportInput.setFieldValue('riSelector', 'dropdown');
        formAUDReportInput.setFieldValue('riDataFormat', 'string');
        formAUDReportInput.setFieldValue('riSourceQuery', '');
        formAUDReportInput.setFieldValue('riMultiSelect', false);
        formAUDReportInput.setFieldValue('riRequired', false);
        formAUDReportInput.setFieldValue('riInPageInput', false);
        formAUDReportInput.setFieldValue('riDefaultValue', '');

        setRISelector('dropdown');
        setRIDataFormat('string');
    };

    const setRIFormFromSelectedReportInput = () => {
        const selectedReportInput = getSelectedReportInput();

        formAUDReportInput.setFieldValue('riTitle', selectedReportInput?.title ?? '');
        formAUDReportInput.setFieldValue('riGlobalDesignator', selectedReportInput?.globalDesignator ?? '');
        formAUDReportInput.setFieldValue('riSelector', selectedReportInput?.selector ?? 'dropdown');
        formAUDReportInput.setFieldValue('riDataFormat', selectedReportInput?.dataFormat ?? 'string');
        formAUDReportInput.setFieldValue('riSourceQuery', selectedReportInput?.sourceQuery ?? '');
        formAUDReportInput.setFieldValue('riMultiSelect', selectedReportInput?.multiSelect ?? false);
        formAUDReportInput.setFieldValue('riRequired', selectedReportInput?.required ?? false);
        formAUDReportInput.setFieldValue('riInPageInput', selectedReportInput?.inPageInput ?? false);
        formAUDReportInput.setFieldValue('riDefaultValue', selectedReportInput?.defaultValue ?? '');

        setRISelector(selectedReportInput?.selector ?? 'dropdown');
        setRIDataFormat(selectedReportInput?.dataFormat ?? 'string');
    };

    const handleRIReportInputResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRIBlankForm();
            return;
        }

        setRIFormFromSelectedReportInput();
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredRIField = (fieldName: string): Promise<void> => {
        const value = formAUDReportInput.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateRIDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'riTitle':
                return validateRequiredRIField(rule.field);

            case 'riSourceQuery':
            case 'riMultiSelect':
            case 'riSelector':
            case 'riDataFormat':
            case 'riRequired':
            case 'riInPageInput':
            case 'riGlobalDesignator':
            case 'riDefaultValue':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const handleRISelectorChange = (value: string) => {
        setRISelector(value);
    };

    const handleRIDataFormatChange = (value: string) => {
        setRIDataFormat(value);
    };

    const buildManagedReportInputRequest = (values: AUDReportInputFormValues): ManagedReportInput => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? riSelectedReportInputID : (delButtonClicked ? riSelectedReportInputID : 0)),
            reportId: riPropsReportID,
            title: values.riTitle,
            sourceQuery: values.riSourceQuery,
            multiSelect: values.riMultiSelect,
            selector: values.riSelector,
            dataFormat: values.riDataFormat,
            required: values.riRequired,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? riSelectedReportInputCreatedBy : (delButtonClicked ? riSelectedReportInputCreatedBy : 0)),
            modifiedBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? (user?.id ?? 0) : (delButtonClicked ? (user?.id ?? 0) : 0)),
            deletedBy: addButtonClicked ? 0 : (updButtonClicked ? 0 : (delButtonClicked ? (user?.id ?? 0) : 0)),
            createdTs: addButtonClicked ? (new Date()) : (updButtonClicked ? riSelectedReportInputCreatedTs : (delButtonClicked ? riSelectedReportInputCreatedTs : undefined)),
            modifiedTs: addButtonClicked ? (new Date()) : (updButtonClicked ? (new Date()) : (delButtonClicked ? (new Date()) : undefined)),
            deletedTs: addButtonClicked ? (undefined) : (updButtonClicked ? (undefined) : (delButtonClicked ? (new Date()) : undefined)),
            globalDesignator: values.riGlobalDesignator,
            inPageInput: values.riInPageInput,
            parentId: -1,
            defaultValue: values.riDefaultValue,
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDReportInputFormValues>) => {
        console.error('AUDReportInputs validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Input Validation',
                'Please correct the highlighted Report Input fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDReportInputFormValues) => {
        const newManagedReportInput = buildManagedReportInputRequest(values);

        props.handleSetReportInputs({
            operation: getRIOperation(),
            reportInput: newManagedReportInput,
        });

        close();
    };

    const handleRISelectionChange = (value: number) => {
        const selectedReportInput = getSelectedReportInput(value);

        setRISelectedReportInputID(value);
        setRISelectedReportInputTitle(selectedReportInput?.title ?? '');

        setRISelectedReportInputCreatedBy(selectedReportInput?.createdBy ?? 0);
        setRISelectedReportInputCreatedTs(selectedReportInput?.createdTs ?? new Date());

        setRIMode('add');
        setRIBlankForm();
    };

    //=================================================================
    //=================================================================
    
    const handleRIAddReportInput = () => {
        clearRISelection();
    };

    //=================================================================
    //=================================================================

    const handleRIUpdReportInput = () => {
        setRIMode('update');
        handleRIReportInputResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRIDelReportInput = () => {
        setRIMode('delete');
        handleRIReportInputResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onRICancel = () => {
        clearRISelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };
    
    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title={aiContextAssistContext.title}
                currentSectionTitle={aiContextAssistContext.currentSectionTitle}
                currentText={aiContextAssistCurrentText}
                requestSectionTitle={aiContextAssistContext.requestSectionTitle}
                requestPlaceholder={aiContextAssistContext.requestPlaceholder}
                resultSectionTitle={aiContextAssistContext.resultSectionTitle}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                ancillarySectionTitle='Ancillary Information'
                loading={aiContextAssistLoading}
                askButtonText={aiContextAssistContext.askButtonText}
                useButtonText={aiContextAssistContext.useButtonText}
                allowUseSuggestion={true}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={handleUseAIContextAssistSuggestion}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <Spin spinning={false}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Inputs</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report: [' + formatRIDisplayID(riPropsReportID) + '] ' + riPropsReportGroupName + '-' + riPropsReportName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Input: [' + formatRIDisplayID(riSelectedReportInputID) + '] ' + riSelectedReportInputTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Report Input to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report Input'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleRISelectionChange}
                        >
                            {reportInputs.map(
                                (eachReportInput: ManagedReportInput) => {
                                    return (
                                        <Option
                                            key={eachReportInput.id}
                                            label={eachReportInput.title}
                                            value={eachReportInput.id}
                                        >
                                            {eachReportInput.title}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIAddReportInput}
                        >
                            Add Report Input
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={riSelectedReportInputID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIUpdReportInput}
                        >
                            Update Report Input
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={riSelectedReportInputID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIDelReportInput}
                        >
                            Delete Report Input
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDReportInput}
                    initialValues={initialRIFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getRIActionColor() }}>
                                    {addButtonClicked ? 'Add Report Input' :
                                        (updButtonClicked ? 'Update Report Input: [' + formatRIDisplayID(riSelectedReportInputID) + '] ' :
                                            (delButtonClicked ? 'Delete Report Input: [' + formatRIDisplayID(riSelectedReportInputID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getRIActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleRIReportInputResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={9}>
                                <Form.Item
                                    key='rititle'
                                    name='riTitle'
                                    label='Title'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            message: 'Title required.',
                                            required: true,
                                            validator: validateRIDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Title'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={9}>
                                <Form.Item
                                    key='riglobaldesignator'
                                    label='Global Designator'
                                    labelCol={{ span: 8, }}
                                    name='riGlobalDesignator'
                                    rules={[
                                        {
                                            validator: validateRIDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Global Designator'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={5}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='riselector'
                                        name='riSelector'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Selector Type:'
                                            placeholder='Select Selector Type'
                                            showSearch
                                            value={riSelector}
                                            onChange={handleRISelectorChange}
                                        >
                                            <Option
                                                value={'between'}
                                                label={'Between'}
                                            >
                                                {'Between'}
                                            </Option>
                                            <Option
                                                value={'datepicker'}
                                                label={'Date Picker'}
                                            >
                                                {'Date Picker'}
                                            </Option>
                                            <Option
                                                value={'dropdown'}
                                                label={'Drop Down'}
                                            >
                                                {'Drop Down'}
                                            </Option>
                                            <Option
                                                value={'numeric'}
                                                label={'Numeric'}
                                            >
                                                {'Numeric'}
                                            </Option>
                                            <Option
                                                value={'text'}
                                                label={'Text'}
                                            >
                                                {'Text'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={5}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='ridataformat'
                                        name='riDataFormat'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Data Format:'
                                            placeholder='Select Data Format'
                                            showSearch
                                            value={riDataFormat}
                                            onChange={handleRIDataFormatChange}
                                        >
                                            <Option
                                                value={'DateTime'}
                                                label={'Date Time'}
                                            >
                                                {'Date Time'}
                                            </Option>
                                            <Option
                                                value={'numeric'}
                                                label={'Numeric'}
                                            >
                                                {'Numeric'}
                                            </Option>
                                            <Option
                                                value={'string'}
                                                label={'String'}
                                            >
                                                {'String'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='rimultiselect'
                                    name='riMultiSelect'
                                    valuePropName='checked'
                                    style={{marginTop: '26px'}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'right'}}
                                    >
                                        Multi Select
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='rirequired'
                                    name='riRequired'
                                    valuePropName='checked'
                                    style={{marginTop: '26px'}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'right'}}
                                    >
                                        Required
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='riinpageinput'
                                    name='riInPageInput'
                                    valuePropName='checked'
                                    style={{marginTop: '26px'}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'right'}}
                                    >
                                        In Page Input
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    label='Source Query'
                                    labelCol={{ span: 4, }}
                                    style={{marginBottom: '4px'}}
                                >
                                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                        <Form.Item
                                            key='risourcequery'
                                            name='riSourceQuery'
                                            rules={[
                                                {
                                                    validator: validateRIDetailsData,
                                                },
                                            ]}
                                            style={{ flex: 1, marginBottom: '0px' }}
                                        >
                                            <Input.TextArea
                                                disabled={delButtonClicked}
                                                placeholder='Enter Source Query'
                                                style={{
                                                    color: delButtonClicked ? 'gray' : 'black',
                                                    height: '64px',
                                                    overflowY: 'auto',
                                                    resize: 'none',
                                                    paddingRight: '12px',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </Form.Item>
                                        <Button
                                            disabled={delButtonClicked || isGetAIProvidersLoading}
                                            icon={<BulbOutlinedIlluminated />}
                                            style={{ minWidth: '32px', padding: '0px', }}
                                            title='AI Assist'
                                            type='default'
                                            onClick={handleOpenAISourceQueryAssistModal}
                                        />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    key='ridefaultvalue'
                                    name='riDefaultValue'
                                    label='Default Value'
                                    labelCol={{ span: 4, }}
                                    rules={[
                                        {
                                            validator: validateRIDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <div style={{ textAlign: 'left', width: '100%' }}>
                                        <Input
                                            disabled={delButtonClicked}
                                            placeholder='Enter Default Value'
                                            style={{ color: delButtonClicked ? 'gray' : 'black', width: '40%', }}
                                        />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='riCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='riApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Report Input' : (updButtonClicked ? 'Update Report Input' : (delButtonClicked ? 'Delete Report Input' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='riCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onRICancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDReportInputs;
